import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { DateTimeFormatter, Instant, LocalDateTime } from "js-joda";
import {
  VictoryArea,
  VictoryAxis,
  VictoryCandlestick,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
  VictoryPortal,
} from "victory";
import LinkedList from "linked-list";

import { formatDateTimeFromApi, instantFromApi, momentFromApi } from "../utils/dateTime";
import { parseCurrencyPairString } from "../utils/botUtils";
import { isTradeClosed } from "./CandleSticksPanel";

@inject("store")
@observer
class CandleStickChart extends Component {
  static createChartXTickValues(startDate, endDate, tickCount = 12) {
    const startDateUnix = startDate.unix();
    const endDateUnix = endDate.unix();

    const partUnix = Math.floor((endDateUnix - startDateUnix) / tickCount);

    const tickValues = [];
    for (let dateUnix = startDateUnix; dateUnix <= endDateUnix; dateUnix += partUnix) {
      tickValues.push(dateUnix);
    }

    return tickValues;
  }

  static findNoTradableTimeAreas(config, trades) {
    const botMaxOpenTrades = config.maxOpenTrades;
    if (botMaxOpenTrades === 1) return [];

    const timeline = CandleStickChart.createTimeLine(trades);

    let openTradesCount = 0;
    let startInstant = null;
    const noTradableArray = [];

    for (let item = timeline.head; item !== null; item = item.next) {
      openTradesCount += item.type === "OPEN" ? 1 : -1;
      if (openTradesCount === botMaxOpenTrades) {
        startInstant = item.instant;
      } else {
        if (startInstant) {
          noTradableArray.push({ start: startInstant.epochSecond(), end: item.instant.epochSecond() });
          startInstant = null;
        }
      }
    }

    return noTradableArray;
  }

  static createTimeLine(trades) {
    const timeline = new LinkedList();

    const now = Instant.now();

    if (trades.length === 0) return timeline;

    for (let trade of trades) {
      const openInstant = instantFromApi(trade.openDate);
      const closeInstant = trade.closeDate ? instantFromApi(trade.closeDate) : now;

      // find open date
      let item;
      for (item = timeline.tail; item !== null && item.instant.isAfter(openInstant); item = item.prev) {}

      let openItem;
      if (item === null) openItem = timeline.prepend(CandleStickChart.createTimelineItem(openInstant, "OPEN", trade));
      else {
        openItem = item.append(CandleStickChart.createTimelineItem(openInstant, "OPEN", trade));
      }

      // find close date
      for (item = openItem.next; item !== null && item.instant.isBefore(closeInstant); item = item.next) {}

      if (item === null) timeline.append(CandleStickChart.createTimelineItem(closeInstant, "CLOSE", trade));
      else {
        item.prepend(CandleStickChart.createTimelineItem(closeInstant, "CLOSE", trade));
      }
    }

    return timeline;
  }

  static createTooltipLabel(date, price, zoneId) {
    return formatDateTimeFromApi(date, zoneId) + ": " + price;
  }

  static createTimelineItem(instant, type, trade) {
    const item = new LinkedList.Item();
    item["instant"] = instant;
    item["type"] = type;
    item["trade"] = trade;
    return item;
  }

  render() {
    const {
      store: { zoneId },
      config,
      startDate,
      endDate,
      candlesData,
      trades,
      currencyPairString,
    } = this.props;

    const currencyPair = parseCurrencyPairString(currencyPairString);

    // filter out trades of non-selected currencies and
    // trades that haven't been executed within selected interval
    const selectedBaseTrades = trades.filter(t => t.base === currencyPair.base).filter(t => {
      const openDate = momentFromApi(t.openDate);
      const closeDate = isTradeClosed(t) ? momentFromApi(t.closeDate) : null;

      return (
        openDate.isAfter(startDate) &&
        openDate.isBefore(endDate) &&
        (!closeDate || (closeDate.isAfter(startDate) && closeDate.isBefore(endDate)))
      );
    });

    const createTradeLine = (trade, color) => {
      const openDate = typeof trade.openDate === "object" ? trade.openDate.epochSecond : trade.openDate;
      const closeDate = typeof trade.closeDate === "object" ? trade.closeDate.epochSecond : trade.closeDate;

      return (
        <VictoryLine
          key={openDate}
          style={{
            data: {
              stroke: color,
              strokeWidth: 2,
              strokeDasharray: "5,2",
              opacity: 0.9,
            },
          }}
          data={[
            { x: openDate, y: trade.openTotal / trade.openAmount },
            { x: closeDate, y: trade.closeTotal / trade.closeAmount },
          ]}
        />
      );
    };

    const createTradeOpenCloseDots = (trade, color) => {
      const dots = [];
      const openDate = typeof trade.openDate === "object" ? trade.openDate.epochSecond : trade.openDate;
      const closeDate =
        isTradeClosed(trade) && typeof trade.closeDate === "object" ? trade.closeDate.epochSecond : trade.closeDate;

      const openPrice = trade.openTotal / trade.openAmount;
      dots.push({ x: openDate, y: openPrice, label: CandleStickChart.createTooltipLabel(openDate, openPrice, zoneId) });

      if (closeDate && trade.closeTotal && trade.closeAmount) {
        const closePrice = trade.closeTotal / trade.closeAmount;
        dots.push({
          x: closeDate,
          y: closePrice,
          label: CandleStickChart.createTooltipLabel(closeDate, closePrice, zoneId),
        });
      }

      return (
        <VictoryScatter
          key={openDate}
          style={{ data: { fill: color, opacity: 0.9 } }}
          size={3}
          data={dots}
          labelComponent={<VictoryTooltip />}
        />
      );
    };

    const profitLines = selectedBaseTrades
      .filter(t => isTradeClosed(t))
      .filter(t => t.profitTotal > 0)
      .map(t => createTradeLine(t, "#009b04"));
    const lossLines = selectedBaseTrades
      .filter(t => isTradeClosed(t))
      .filter(t => t.profitTotal < 0)
      .map(t => createTradeLine(t, "#a80025"));

    const dots = selectedBaseTrades.map(t => createTradeOpenCloseDots(t, "#000000"));

    // let's create the no tradable areas
    const noTradableAreaList = CandleStickChart.findNoTradableTimeAreas(config, trades);

    // find the minimum and the maximum price in the chart
    const minMax =
      candlesData.length <= 0
        ? []
        : candlesData.reduce(
            (acc, candle) => {
              return [Math.min(candle.low, acc[0]), Math.max(candle.high, acc[1])];
            },
            [Number.MAX_VALUE, Number.MIN_VALUE]
          );

    const startDateEpochSeconds = startDate.unix(),
      endDateEpochSeconds = endDate.unix();

    // create the chart areas
    const nonTradableAreas =
      candlesData.length <= 0
        ? []
        : noTradableAreaList
            .filter(z => {
              return (
                z.start > startDateEpochSeconds &&
                z.start < endDateEpochSeconds &&
                z.end > startDateEpochSeconds &&
                z.end < endDateEpochSeconds
              );
            })
            .map(timeArea => {
              return (
                <VictoryArea
                  key={timeArea.start}
                  domain={{ y: [minMax[0], minMax[1]] }}
                  style={{ data: { fill: "#fc61ef", opacity: 0.2 } }}
                  data={[
                    { x: timeArea.start, y: minMax[1], y0: minMax[0] },
                    { x: timeArea.end, y: minMax[1], y0: minMax[0] },
                  ]}
                />
              );
            });

    const tickValues = CandleStickChart.createChartXTickValues(startDate, endDate);

    const selectedBaseTradesSortedByOpenDate = selectedBaseTrades.sort((a, b) =>
      instantFromApi(a.openDate).compareTo(instantFromApi(b.openDate))
    );

    return (
      <VictoryChart
        width={1400}
        height={500}
        theme={VictoryTheme.material}
        domainPadding={20}
        style={{
          // This is a dirty hack to re-render the chart on zoneId change
          parent: { whiteSpace: zoneId },
        }}
      >
        <VictoryAxis
          tickValues={tickValues}
          tickFormat={dEpoch =>
            LocalDateTime.ofInstant(Instant.ofEpochSecond(dEpoch), zoneId).format(
              DateTimeFormatter.ofPattern("d.M. HH:mm")
            )
          }
        />
        <VictoryAxis
          dependentAxis
          tickLabelComponent={
            <VictoryPortal>
              <VictoryLabel
                text={d => {
                  const price =
                    candlesData.length <= 0
                      ? null
                      : selectedBaseTradesSortedByOpenDate.length > 0
                        ? selectedBaseTradesSortedByOpenDate[0].openTotal /
                          selectedBaseTradesSortedByOpenDate[0].openAmount
                        : minMax[0];

                  const percents = !price ? "" : ((d / price - 1) * 100).toFixed(1) + "%";

                  return d + "\n" + percents;
                }}
              />
            </VictoryPortal>
          }
        />

        <VictoryCandlestick candleColors={{ positive: "#b3dda9", negative: "#ff9ea2" }} data={candlesData} />

        {profitLines}
        {lossLines}
        {nonTradableAreas}
        {dots}
      </VictoryChart>
    );
  }
}

export default CandleStickChart;



// WEBPACK FOOTER //
// ./src/charts/CandleStickChart.js